import { Link } from 'react-router-dom';
import type { DeviceModel, DeviceUnit, Installation, SubmissionRejection } from '@regigov/contracts';
import { StatusBadge, formatDateTime } from './ui';

export function ModelsTable({ items, basePath }: { items: DeviceModel[]; basePath: string }) {
  if (items.length === 0) return <p className="muted">No models match.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Model</th>
          <th>Class</th>
          <th>Manufacturer</th>
          <th>Status</th>
          <th>Units</th>
          <th>Rules</th>
          <th>Model ID</th>
        </tr>
      </thead>
      <tbody>
        {items.map((m) => (
          <tr key={m.id}>
            <td>
              <Link to={`${basePath}/models/${m.id}`}>{m.modelNumber}</Link> <span className="muted">{m.name}</span>
            </td>
            <td>{m.deviceClass}</td>
            <td>{m.manufacturerName}</td>
            <td>
              <StatusBadge status={m.status} />
            </td>
            <td>{m.unitCount}</td>
            <td>v{m.ruleVersionApplied}</td>
            <td>
              <code>{m.id}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function UnitsTable({ items, basePath }: { items: DeviceUnit[]; basePath: string }) {
  if (items.length === 0) return <p className="muted">No units match.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Serial</th>
          <th>Model</th>
          <th>Owner</th>
          <th>Status</th>
          <th>Compliance</th>
          <th>Batch</th>
          <th>Unit ID</th>
        </tr>
      </thead>
      <tbody>
        {items.map((u) => (
          <tr key={u.id}>
            <td>
              <Link to={`${basePath}/units/${u.id}`}>{u.serial}</Link>
            </td>
            <td>
              {u.modelNumber} <span className="muted">({u.deviceClass})</span>
            </td>
            <td>{u.ownerName}</td>
            <td>
              <StatusBadge status={u.status} />
            </td>
            <td>
              <StatusBadge status={u.complianceStatus} />
            </td>
            <td>{u.batch ?? <span className="muted">—</span>}</td>
            <td>
              <code>{u.id}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function InstallationsTable({ items, basePath }: { items: Installation[]; basePath: string }) {
  if (items.length === 0) return <p className="muted">No installations match.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>NMI</th>
          <th>Unit</th>
          <th>Site</th>
          <th>Installer</th>
          <th>Status</th>
          <th>Commissioned</th>
          <th>Installation ID</th>
        </tr>
      </thead>
      <tbody>
        {items.map((i) => (
          <tr key={i.id}>
            <td>
              <Link to={`${basePath}/installations/${i.id}`}>{i.nmi}</Link>
            </td>
            <td>
              <Link to={`${basePath}/units/${i.unitId}`}>{i.serial}</Link> <span className="muted">{i.modelNumber}</span>
            </td>
            <td>
              {i.address.suburb} {i.address.state} {i.address.postcode}
            </td>
            <td>{i.installerName}</td>
            <td>
              <StatusBadge status={i.status} />
            </td>
            <td>{i.commissionedOn}</td>
            <td>
              <code>{i.id}</code>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function RejectionsTable({ items, showParticipant }: { items: SubmissionRejection[]; showParticipant: boolean }) {
  if (items.length === 0) return <p className="muted">No rejected submissions.</p>;
  return (
    <table className="table">
      <thead>
        <tr>
          <th>When</th>
          <th>Kind</th>
          <th>Class / rules</th>
          {showParticipant && <th>Participant</th>}
          <th>Submitted by</th>
          <th>Reasons</th>
        </tr>
      </thead>
      <tbody>
        {items.map((r) => (
          <tr key={r.id}>
            <td>{formatDateTime(r.createdAt)}</td>
            <td>{r.entityKind}</td>
            <td>
              {r.deviceClass ?? '—'} {r.ruleVersion !== null && <span className="muted">v{r.ruleVersion}</span>}
            </td>
            {showParticipant && <td>{r.participantName ?? <span className="muted">—</span>}</td>}
            <td>
              {r.submittedByName ?? <span className="muted">{r.sourceChannel}</span>}
            </td>
            <td>
              <ul className="reasons">
                {r.reasons.map((x, i) => (
                  <li key={i}>
                    {x.field && <code>{x.field}</code>} {x.message} <span className="muted">[{x.code}]</span>
                  </li>
                ))}
              </ul>
              <details>
                <summary className="muted">payload</summary>
                <pre>{JSON.stringify(r.payload, null, 2)}</pre>
              </details>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
